import { useState, useRef, useEffect, useMemo } from 'react';
import { DayPicker } from 'react-day-picker';
import { usePopper } from 'react-popper';
import { CryptoTransactionTypes, TransactionTypes } from '../../../client/Juno/client/constant';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClearIcon from '@mui/icons-material/Clear';
import JunoButton from '../common/JunoButton';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useJunoCommonFunctions } from '../../../client/Juno/helpers';
import { useSelector } from 'react-redux';
import { useOutsideClick } from '../../hooks/useOutsideClick';
import CustomMultiSelect from '../common/CustomeMultiSelect';
import { cryptoCurrencyList, fiatCurrencyList } from '../../helpers/constant';

const FocusTrap = ({ children, active, onDeactivate }) => {
    const trapRef = useRef(null);

    useEffect(() => {
        if (!active) return;

        const handleKeyDown = e => {
            if (e.key === 'Escape') {
                onDeactivate?.();
            }
        };

        const handleClickOutside = e => {
            if (trapRef.current && !trapRef.current.contains(e.target)) {
                onDeactivate?.();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [active, onDeactivate]);

    return <div ref={trapRef}>{children}</div>;
};

function FilterPanel({ onApply, currentFilters, filterOptions, getStatus, getStatusCodes }) {
    const [tempFilters, setTempFilters] = useState(
        currentFilters || {
            currency: [],
            currencyType: [],
            type: [],
            from: '',
            to: '',
            status: [],
            statusDisplay: [],
        },
    );
    const activeCurrencyPage = useSelector(state => state.juno.activeCurrencyPage);
    const popperRef = useRef(null);
    const buttonRef = useRef(null);
    const [popperElement, setPopperElement] = useState(null);
    const [isPopperOpen, setIsPopperOpen] = useState(false);
    const { useWindowWidth } = useJunoCommonFunctions();
    const windowWidth = useWindowWidth();
    const imageBaseUrl = useSelector(state => state.config.image_base_url);
    const wrapperRef = useRef(null);

    const activePageCurrencyList = activeCurrencyPage == 'fiat' ? fiatCurrencyList : cryptoCurrencyList;

    useOutsideClick(wrapperRef, () => {
        if (isPopperOpen) setIsPopperOpen(false);
    });

    const popper = usePopper(popperRef.current, popperElement, {
        placement: 'bottom-start',
    });
    const closePopper = () => {
        setIsPopperOpen(false);
    };
    const handleDateClick = e => {
        e.stopPropagation();
        if (isPopperOpen) {
            closePopper();
        } else {
            setIsPopperOpen(true);
        }
    };

    const handleLocalDateSelect = range => {
        if (!range) return;

        if (range.from && range.to) {
            const updatedFilters = {
                ...tempFilters,
                from: range.from,
                to: range.to,
            };
            setTempFilters(updatedFilters);
        } else if (range.from) {
            setTempFilters(prev => ({
                ...prev,
                from: range.from,
                to: '',
            }));
        }
    };

    const handleLocalStatusChange = selectedStatusArray => {
        if (!selectedStatusArray || selectedStatusArray.length === 0) {
            setTempFilters(prev => ({ ...prev, status: [], statusDisplay: [] }));
            return;
        }

        if (selectedStatusArray.includes('ALL')) {
            setTempFilters(prev => ({ ...prev, status: [], statusDisplay: [] }));
            return;
        }

        const statusDisplay = [...selectedStatusArray];

        let statusCodes = [];
        if (getStatusCodes) {
            selectedStatusArray.forEach(status => {
                const codes = getStatusCodes(status);
                if (Array.isArray(codes)) {
                    statusCodes = [...statusCodes, ...codes];
                } else {
                    statusCodes.push(codes);
                }
            });
        } else {
            statusCodes = selectedStatusArray;
        }

        setTempFilters(prev => ({
            ...prev,
            status: statusCodes,
            statusDisplay: statusDisplay,
        }));
    };

    const handleTempFilterChange = (key, value) => {
        if (key === 'type') {
            if (!value || value.length === 0) {
                setTempFilters(prev => ({ ...prev, [key]: [] }));
                return;
            }

            if (Array.isArray(value) && value.includes('ALL')) {
                setTempFilters(prev => ({ ...prev, [key]: [] }));
                return;
            }
        }

        setTempFilters(prev => ({ ...prev, [key]: value }));
    };

    // Handle currency type change with currency reset
    const handleCurrencyTypeChange = value => {
        setTempFilters(prev => ({
            ...prev,
            currencyType: value === 'ALL' ? [] : [value],
            currency: [], // Reset currency when currency type changes
        }));
    };

    const handleReset = () => {
        const defaultFilters = {
            currency: [],
            currencyType: [],
            type: [],
            from: '',
            to: '',
            status: [],
            statusDisplay: [],
        };
        setTempFilters(defaultFilters);
    };

    const handleSave = () => {
        onApply(tempFilters);
    };

    const getCurrentDisplayText = () => {
        const { from, to } = tempFilters;
        if (!from && !to) return 'Date';

        const fromDate = from ? new Date(from).toLocaleDateString() : '';
        const toDate = to ? new Date(to).toLocaleDateString() : '';

        return from && to ? `${fromDate} - ${toDate}` : fromDate || toDate;
    };

    const getCurrentStatusDisplay = () => {
        if (tempFilters.status && tempFilters.status.length > 0) {
            return getStatus ? getStatus(tempFilters.status[0]) : tempFilters.status[0];
        }
        return 'ALL';
    };

    const getTransactionTypes = () => {
        let options = [];
        if (activeCurrencyPage === 'fiat') {
            options = TransactionTypes;
        } else {
            options = CryptoTransactionTypes;
        }

        return options;
    };

    return (
        <div
            className={`${
                windowWidth > 1100
                    ? 'absolute top-full right-0 mt-2 bg-filterPannel-bg border !border-section-br rounded-lg shadow-xl z-50  w-[70vw]'
                    : windowWidth < 768
                      ? 'flex flex-col pb-10 justify-between h-full'
                      : null
            }`}>
            <div className={windowWidth > 1100 ? 'p-6 w-full' : 'w-full '}>
                <div
                    className={` ${
                        windowWidth > 1100
                            ? 'grid grid-cols-4 gap-4 items-start'
                            : 'grid grid-flow-row grid-cols-1 gap-4 items-center py-3'
                    }`}>
                    {/* TIME & DATE */}
                    <div className="space-y-1">
                        <label className="text-xs font-medium text-section-title uppercase">
                            TIME & DATE
                        </label>
                        <div
                            onClick={event => {
                                event.stopPropagation();
                            }}
                            className="relative"
                            ref={wrapperRef}>
                            <div className="w-full">
                                <button
                                    type="button"
                                    className="w-full appearance-none bg-card-bg rounded-lg px-3 py-3 text-sm text-text cursor-pointer text-left"
                                    onClick={handleDateClick}
                                    ref={buttonRef}>
                                    <div className="flex justify-between items-center">
                                        <span
                                            className={
                                                getCurrentDisplayText() === 'Date'
                                                    ? 'text-table-col-title'
                                                    : null
                                            }>
                                            {getCurrentDisplayText()}
                                        </span>
                                        {getCurrentDisplayText() === 'Date' ? (
                                            <ExpandMoreIcon
                                                fontSize="small"
                                                className="absolute w-3 h-3 text-balanceCard-manage right-5"
                                            />
                                        ) : (
                                            <ClearIcon
                                                fontSize="small"
                                                className="w-4 h-4 text-balanceCard-manage cursor-pointer"
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    setTempFilters(pre => ({
                                                        ...pre,
                                                        from: '',
                                                        to: '',
                                                    }));
                                                }}
                                            />
                                        )}
                                    </div>
                                </button>

                                {isPopperOpen && (
                                    <FocusTrap active={isPopperOpen}>
                                        <div
                                            tabIndex={-1}
                                            style={popper.styles.popper}
                                            className="!bg-filterPannel-bg rounded-lg shadow-lg z-50 mt-[54px]"
                                            {...popper.attributes.popper}
                                            ref={setPopperElement}
                                            role="dialog"
                                            aria-label="DayPicker calendar">
                                            <DayPicker
                                                className="!bg-filterPannel-bg rounded-lg !text-primary"
                                                classNames={{
                                                    dropdown: '!bg-filterPannel-bg rdp-dropdown',
                                                }}
                                                initialFocus={isPopperOpen}
                                                mode="range"
                                                selected={{
                                                    from: tempFilters.from,
                                                    to: tempFilters.to,
                                                }}
                                                onSelect={handleLocalDateSelect}
                                                fromYear={2022}
                                                toDate={new Date()}
                                                captionLayout="dropdown"
                                            />
                                        </div>
                                    </FocusTrap>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* CURRENCY */}
                    <div
                        onClick={event => {
                            event.stopPropagation();
                        }}>
                        <CustomMultiSelect
                            label="CURRENCY"
                            placeholder="ALL"
                            value={tempFilters.currency}
                            onChange={selected => handleTempFilterChange('currency', selected)}
                            options={activePageCurrencyList}
                        />
                    </div>

                    {/* STATUS */}
                    <CustomMultiSelect
                        label="STATUS"
                        placeholder="ALL"
                        value={tempFilters.statusDisplay || []}
                        onChange={handleLocalStatusChange}
                        options={filterOptions.statusOptions}
                    />

                    {/* TRANSACTION TYPE */}
                    <CustomMultiSelect
                        label="TRANSACTION TYPE"
                        placeholder="ALL"
                        value={tempFilters.type}
                        onChange={selected => handleTempFilterChange('type', selected)}
                        options={getTransactionTypes()}
                    />
                </div>
            </div>
            <div
                className={clsx(
                    'flex items-center justify-end gap-2 bg-filterPannel-bg rounded-b-lg',
                    windowWidth > 1100 ? 'p-3 border-t !border-secondary' : 'py-3',
                )}>
                {windowWidth > 1100 ? (
                    <JunoButton
                        type="secondary"
                        onClick={handleReset}
                        className="!w-auto px-6 py-2 text-sm font-medium rounded-lg">
                        RESET
                    </JunoButton>
                ) : (
                    <div
                        className="w-12 h-12 p-2 rounded-lg !bg-secondary cursor-pointer"
                        onClick={handleReset}>
                        <img
                            alt="resetIcon"
                            onClick={handleReset}
                            data-e2e="clear-all"
                            src={`${imageBaseUrl}/webUI/resetIcon.svg`}
                            className="w-full h-full invert-0 dark:invert"
                        />
                    </div>
                )}

                <JunoButton
                    type="primary"
                    onClick={handleSave}
                    className={clsx(
                        'text-sm !text-secondary !bg-primary hover:bg-hover-bg rounded-lg transition-colors',
                        windowWidth > 1100
                            ? '!w-auto px-6 py-2 font-medium'
                            : 'px-10 py-3 w-full font-semibold',
                    )}>
                    SAVE
                </JunoButton>
            </div>
        </div>
    );
}

FocusTrap.propTypes = {
    children: PropTypes.node.isRequired,
    active: PropTypes.bool.isRequired,
    onDeactivate: PropTypes.func,
};

FilterPanel.propTypes = {
    onApply: PropTypes.func.isRequired,
    currentFilters: PropTypes.shape({
        currency: PropTypes.arrayOf(
            PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.shape({
                    currencyShortName: PropTypes.string,
                    currencyIcon: PropTypes.string,
                    currencyFullName: PropTypes.string,
                }),
            ]),
        ),
        currencyType: PropTypes.arrayOf(PropTypes.string),
        type: PropTypes.arrayOf(PropTypes.string),
        from: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
        to: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
        status: PropTypes.arrayOf(PropTypes.string),
        statusDisplay: PropTypes.arrayOf(PropTypes.string), // Add this to PropTypes
    }),
    filterOptions: PropTypes.shape({
        currencyTypes: PropTypes.arrayOf(PropTypes.string),
        statusOptions: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
    getStatus: PropTypes.func,
    getStatusCodes: PropTypes.func,
    balancesFiatData: PropTypes.array,
    balancesCryptoData: PropTypes.array,
    clientData: PropTypes.shape({
        fiatTab: PropTypes.string,
        cryptoTab: PropTypes.string,
    }),
    filterBalances: PropTypes.func,
};

export default FilterPanel;
